import type { SubjectOf } from './subjects'
import { malformed, readJson, request } from './request'

export interface Tag {
  readonly id: string
  readonly name: string
}
export interface Flashcard {
  readonly id: string
  readonly front: string
  readonly back: string
  readonly askedFor: string
  readonly frontFormat: string
  readonly backFormat: string
  readonly reversible: boolean
  readonly tags: readonly Tag[]
  readonly createdAt: string
  readonly updatedAt: string
  /**
   * The subjects this card belongs to.
   *
   * Beside the tags rather than instead of them: a tag comes into being in passing while typing and
   * belongs to the cards alone, a subject is the bracket the whole of MyLib shares.
   */
  readonly subjects?: readonly SubjectOf[]
}
export type TagMode = 'all' | 'any'
export interface CardFilter {
  readonly query: string
  readonly tags: readonly string[]
  readonly tagMode: TagMode
}
export interface SavedCardFilter {
  readonly id: string
  readonly name: string
  readonly filter: CardFilter
  readonly createdAt: string
  readonly updatedAt: string
}
export type LearningStatus = 'active' | 'completed' | 'aborted'
export interface LearningItem {
  readonly id: string
  readonly cardId: string
  readonly direction: 'forward' | 'reverse'
  readonly question: string
  readonly answer: string
  readonly askedFor: string
  readonly questionFormat: string
  readonly answerFormat: string
  readonly state: 'question' | 'answer' | 'finished'
  readonly shownForMs: number
  readonly deferrals: number
}
export interface LearningRun {
  readonly id: string
  readonly status: LearningStatus
  readonly startedAt: string
  readonly endedAt: string | null
  readonly infinite: boolean
  readonly answered: number
  readonly total: number
  readonly current: LearningItem | null
  readonly kind: LearningMode | 'manual'
  readonly scopeKey: string | null
}
export type LearningMode = 'automatic' | 'gaps' | 'continue'
export type LearningReason =
  | 'new_variant'
  | 'never_selected_in_set'
  | 'long_not_tested'
  | 'useful_review_due'
  | 'recent_failure'
  | 'repeated_failure'
  | 'failed_after_long_gap'
  | 'positive_long_gap'
  | 'low_progress'
  | 'repeatedly_skipped_in_set'
  | 'broaden_set_coverage'
  | 'recently_selected_in_set'
export interface LearningPreference {
  readonly roundSize: number
  readonly minimumReviewPercentage: number
  readonly drillRepetitions: number
}
export interface LearningModeOption {
  readonly mode: LearningMode
  readonly candidateCount: number
  readonly selectedCount: number
  readonly urgent: boolean
  readonly reasons: readonly LearningReason[]
}
export interface AutomaticLearningOptions {
  readonly scopeKey: string
  readonly filter: CardFilter
  readonly learningProgress: number
  readonly totalVariants: number
  readonly reviewedVariants: number
  readonly newVariants: number
  readonly nextUsefulAt: string | null
  readonly preference: LearningPreference
  readonly modes: readonly LearningModeOption[]
}
export interface AutomaticLearningParameters {
  readonly roundSize: number
  readonly minimumReviewPercentage: number
}
/** Every learning setting in one place, shared by the round options and the drill. */
export interface LearningParameters {
  readonly roundSize: number
  readonly minimumReviewPercentage: number
  readonly drillRepetitions: number
}
export interface LearningRank {
  readonly cardId: string
  readonly direction: 'forward' | 'reverse'
  readonly learningProgress: number
  readonly selectionScore: number
  readonly relativeRank: number
  readonly memorySpanSeconds: number
  readonly lastAnsweredAt: string | null
  readonly nextUsefulAt: string
  readonly reviewed: boolean
  readonly algorithmVersion: string
  readonly reasons: readonly LearningReason[]
  readonly assessedAttempts: number
  readonly incorrectAttempts: number
  /** Empty unless this variant is a tough nut — the reasons a marking can be understood from. */
  readonly toughReasons: readonly ToughReason[]
  readonly toughStage: ToughStage | null
}
export type LearningSummaryOutcome = 'incorrect' | 'unanswered' | 'correct'
export interface LearningSummaryCard {
  readonly cardId: string
  readonly direction: 'forward' | 'reverse'
  readonly outcome: LearningSummaryOutcome
  readonly question: string
  readonly answer: string
  readonly questionFormat: string
  readonly answerFormat: string
  readonly incorrectAttempts: number
}
export interface LearningRunSummary {
  readonly runId: string
  readonly cards: readonly LearningSummaryCard[]
}
export interface CardDraft {
  readonly front: string
  readonly back: string
  readonly askedFor: string
  readonly reversible: boolean
  readonly tags: readonly string[]
  /** The markups the two sides are written in, as media types. */
  readonly frontFormat: string
  readonly backFormat: string
}
export type ToughReason =
  | 'never_answered'
  | 'no_clue_when_asked'
  | 'recent_failures'
  | 'repeated_failure_across_runs'
  | 'progress_stuck'
  | 'relapse_after_relearn'
export type ToughStage = 'nothing_yet' | 'awaiting_retrieval' | 'acquired_earlier'
export interface AcquisitionRound {
  readonly id: string
  readonly cardId: string
  readonly kind: 'on_paper' | 'typed'
  readonly line: string
  readonly repetitionsTarget: number
  readonly repetitionsDone: number
  readonly mistakes: number
  readonly completed: boolean
  readonly startedAt: string
  readonly endedAt: string | null
}
export interface ToughCard {
  readonly cardId: string
  readonly direction: 'forward' | 'reverse'
  readonly stage: ToughStage
  readonly reasons: readonly ToughReason[]
  readonly question: string
  readonly answer: string
  readonly questionFormat: string
  readonly answerFormat: string
  readonly learningProgress: number
  readonly incorrectAttempts: number
  readonly selectionScore: number
  readonly algorithmVersion: string
  readonly lastAcquisition: AcquisitionRound | null
}

const CARDS = '/api/flashcards'

export async function fetchCards(signal?: AbortSignal): Promise<Flashcard[]> {
  const value = await readJson<unknown>(await request(CARDS, { signal }))
  if (!Array.isArray(value)) throw malformed('The answer did not describe flashcards.')
  return value as Flashcard[]
}
export async function fetchTags(signal?: AbortSignal): Promise<Tag[]> {
  const value = await readJson<unknown>(await request(`${CARDS}/tags`, { signal }))
  if (!Array.isArray(value)) throw malformed('The answer did not describe tags.')
  return value as Tag[]
}
export async function createCard(draft: CardDraft): Promise<Flashcard> {
  return change(CARDS, 'POST', draft) as Promise<Flashcard>
}
export async function updateCard(id: string, draft: CardDraft): Promise<Flashcard> {
  return change(`${CARDS}/${encodeURIComponent(id)}`, 'PUT', draft) as Promise<Flashcard>
}
export async function deleteCard(id: string): Promise<void> {
  await change(`${CARDS}/${encodeURIComponent(id)}`, 'DELETE')
}
export async function similarCards(
  front: string,
  back: string,
  except?: string,
  signal?: AbortSignal,
): Promise<Flashcard[]> {
  const response = await request(`${CARDS}/similar`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ front, back, except }),
    signal,
  })
  return readJson<Flashcard[]>(response)
}
export async function fetchCardFilters(signal?: AbortSignal): Promise<SavedCardFilter[]> {
  return readJson<SavedCardFilter[]>(await request(`${CARDS}/filters`, { signal }))
}
export async function saveCardFilter(name: string, filter: CardFilter): Promise<SavedCardFilter> {
  return change(`${CARDS}/filters`, 'POST', { name, filter }) as Promise<SavedCardFilter>
}
export async function deleteCardFilter(id: string): Promise<void> {
  await change(`${CARDS}/filters/${encodeURIComponent(id)}`, 'DELETE')
}
export async function startLearningRun(
  filter: CardFilter,
  infinite: boolean,
): Promise<LearningRun> {
  return change(`${CARDS}/runs`, 'POST', { filter, infinite }) as Promise<LearningRun>
}
export async function fetchAutomaticLearningOptions(
  filter: CardFilter,
  filterId?: string,
  signal?: AbortSignal,
): Promise<AutomaticLearningOptions> {
  const response = await request(`${CARDS}/learning/options`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filter, filterId }),
    signal,
  })
  return readJson<AutomaticLearningOptions>(response)
}
export async function startAutomaticLearning(
  filter: CardFilter,
  parameters: AutomaticLearningParameters,
  infinite: boolean,
  filterId?: string,
): Promise<LearningRun> {
  return startIntelligent('automatic', filter, parameters, infinite, filterId)
}
export async function startGapLearning(
  filter: CardFilter,
  roundSize: number,
  infinite: boolean,
  filterId?: string,
): Promise<LearningRun> {
  return startIntelligent('gaps', filter, { roundSize }, infinite, filterId)
}
export async function startContinueLearning(
  filter: CardFilter,
  parameters: AutomaticLearningParameters,
  infinite: boolean,
  filterId?: string,
): Promise<LearningRun> {
  return startIntelligent('continue', filter, parameters, infinite, filterId)
}
export async function fetchToughCards(
  filter: CardFilter,
  filterId?: string,
  signal?: AbortSignal,
): Promise<ToughCard[]> {
  const response = await request(`${CARDS}/learning/tough`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filter, filterId }),
    signal,
  })
  return readJson<ToughCard[]>(response)
}
export async function fetchLearningRanks(
  filter: CardFilter,
  filterId?: string,
  signal?: AbortSignal,
): Promise<LearningRank[]> {
  const response = await request(`${CARDS}/learning/ranks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filter, filterId }),
    signal,
  })
  return readJson<LearningRank[]>(response)
}
/** "I know this one": takes the card out of the learning mode until the history says otherwise. */
export async function noLearningNeeded(cardId: string): Promise<void> {
  await change(`${CARDS}/${encodeURIComponent(cardId)}/no-learning-needed`, 'POST')
}
export async function recordAcquisition(
  cardId: string,
  repetitions: number,
  line: string,
): Promise<AcquisitionRound> {
  return change(`${CARDS}/${encodeURIComponent(cardId)}/acquisition`, 'POST', {
    repetitions,
    line,
  }) as Promise<AcquisitionRound>
}
export async function startDrill(
  cardId: string,
  line: string,
  repetitions: number,
): Promise<AcquisitionRound> {
  return change(`${CARDS}/${encodeURIComponent(cardId)}/drill`, 'POST', {
    line,
    repetitions,
  }) as Promise<AcquisitionRound>
}
export async function finishDrill(
  roundId: string,
  repetitionsDone: number,
  mistakes: number,
  lastTyped: string,
): Promise<AcquisitionRound> {
  return change(`${CARDS}/drills/${encodeURIComponent(roundId)}/finish`, 'POST', {
    repetitionsDone,
    mistakes,
    lastTyped,
  }) as Promise<AcquisitionRound>
}
export async function resetAutomaticLearningPreferences(): Promise<LearningPreference> {
  return change(`${CARDS}/learning/preferences`, 'DELETE') as Promise<LearningPreference>
}
export async function fetchLearningRun(id: string, signal?: AbortSignal): Promise<LearningRun> {
  return readJson<LearningRun>(await request(`${CARDS}/runs/${encodeURIComponent(id)}`, { signal }))
}
export async function fetchLearningSummary(
  id: string,
  signal?: AbortSignal,
): Promise<LearningRunSummary> {
  return readJson<LearningRunSummary>(
    await request(`${CARDS}/runs/${encodeURIComponent(id)}/summary`, { signal }),
  )
}
export async function deferLearningCard(id: string, shownForMs: number): Promise<LearningRun> {
  return change(`${CARDS}/runs/${encodeURIComponent(id)}/defer`, 'POST', {
    shownForMs,
  }) as Promise<LearningRun>
}
export async function revealLearningCard(id: string, shownForMs: number): Promise<LearningRun> {
  return change(`${CARDS}/runs/${encodeURIComponent(id)}/reveal`, 'POST', {
    shownForMs,
  }) as Promise<LearningRun>
}
export async function answerLearningCard(id: string, correct: boolean): Promise<LearningRun> {
  return change(`${CARDS}/runs/${encodeURIComponent(id)}/answer`, 'POST', {
    correct,
  }) as Promise<LearningRun>
}
/** "No clue": the card leaves this round and goes to the learning mode. */
export async function noClue(id: string): Promise<LearningRun> {
  return change(`${CARDS}/runs/${encodeURIComponent(id)}/no-clue`, 'POST') as Promise<LearningRun>
}
export async function abortLearningRun(id: string): Promise<LearningRun> {
  return change(`${CARDS}/runs/${encodeURIComponent(id)}/abort`, 'POST') as Promise<LearningRun>
}

async function startIntelligent(
  mode: LearningMode,
  filter: CardFilter,
  parameters: { readonly roundSize: number; readonly minimumReviewPercentage?: number },
  infinite: boolean,
  filterId?: string,
): Promise<LearningRun> {
  return change(`${CARDS}/runs/${mode}`, 'POST', {
    filter,
    filterId,
    infinite,
    ...parameters,
  }) as Promise<LearningRun>
}

async function change(address: string, method: string, body?: unknown): Promise<unknown> {
  const response = await request(address, {
    method,
    expect: [400, 404, 409, 413],
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })
  if (!response.ok) {
    const problem = (await response
      .json()
      .catch(() => ({ message: 'MyLib could not do that.' }))) as { message?: string }
    throw new Error(problem.message ?? 'MyLib could not do that.')
  }
  return response.status === 204 ? null : readJson<unknown>(response)
}
